import React, { useEffect } from 'react'
import { Grid } from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import useAttributes from 'utils/hooks/useAttributes'

import PostponedFormActionButtons from '../PostponedFormActionButtons'
import { MAX_POSTPONED_CONTENT_WIDTH, PostponedFormStepProps } from '..'

import {
  POSTPONED_FORM_ATTRIBUTES,
  useAuth,
} from 'utils/providers/AuthProvider'

import { validationSchema } from './config'

import { CatalogItem, ProfileAttribute, ReactSelect } from 'shared'
import { getCatalogOptions } from 'utils/helpers'
import {
  getAttrSelectValue,
  onChangeAttribute,
} from 'screens/Profile/infoBlocks/helpers'

interface ProfileFormValues {
  tops_size: ProfileAttribute | null
  suits_size: ProfileAttribute | null
  pants_size_m: ProfileAttribute | null
  pants_size_w: ProfileAttribute | null
  shoe_size_new: ProfileAttribute | null
  dress_size: ProfileAttribute | null
  foot_width: ProfileAttribute | null
  pants_type: ProfileAttribute | null
  ear_piercings: ProfileAttribute | null
  ear_piercing_types: ProfileAttribute | null
  body_piercings: ProfileAttribute | null
  height: ProfileAttribute | null
  weight: ProfileAttribute | null
  body_type: ProfileAttribute | null
  bra_band_size: ProfileAttribute | null
  bra_cup_size: ProfileAttribute | null
  shapewear_size: ProfileAttribute | null
}

const SizingPreferencesForm = ({
  onStepChange,
  step,
  updateProfile,
}: PostponedFormStepProps) => {
  const { attributes, isLoading } = useAttributes(
    POSTPONED_FORM_ATTRIBUTES.sizing_preferences
  )

  const { user } = useAuth()

  const {
    pants_size_w,
    pants_size_m,
    tops_size,
    suits_size,
    shoe_size_new,
    dress_size,
    foot_width,
    pants_type,
    ear_piercings,
    ear_piercing_types,
    body_piercings,
    height,
    weight,
    body_type,
    bra_band_size,
    bra_cup_size,
    shapewear_size,
  } = attributes

  const { control, handleSubmit, reset } = useForm<ProfileFormValues>({
    //@ts-expect-error yup doesn't want to work with null but that's okay
    resolver: yupResolver(validationSchema),
  })

  useEffect(() => {
    const profile = user?.profile
    const getProfileAttrValue = (attr: string) => {
      return profile?.attributes.find(({ type }) => type === attr)
    }
    if (!profile) return

    reset({
      tops_size: getProfileAttrValue('tops_size'),
      suits_size: getProfileAttrValue('suits_size'),
      pants_size_m: getProfileAttrValue('pants_size_m'),
      pants_size_w: getProfileAttrValue('pants_size_w'),
      shoe_size_new: getProfileAttrValue('shoe_size_new'),
      dress_size: getProfileAttrValue('dress_size'),
      foot_width: getProfileAttrValue('foot_width'),
      pants_type: getProfileAttrValue('pants_type'),
      ear_piercings: getProfileAttrValue('ear_piercings'),
      ear_piercing_types: getProfileAttrValue('ear_piercing_types'),
      body_piercings: getProfileAttrValue('body_piercings'),
      height: getProfileAttrValue('height'),
      weight: getProfileAttrValue('weight'),
      body_type: getProfileAttrValue('body_type'),
      bra_band_size: getProfileAttrValue('bra_band_size'),
      bra_cup_size: getProfileAttrValue('bra_cup_size'),
      shapewear_size: getProfileAttrValue('shapewear_size'),
    })
  }, [user])

  const onSubmit = handleSubmit((data) => {
    const attributesWithValue = Object.values(data).filter((el) => el?.value)
    if (!attributesWithValue?.length) return
    updateProfile(attributesWithValue)
  })

  if (isLoading) return null

  return (
    <>
      <Grid
        container
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={{ maxWidth: MAX_POSTPONED_CONTENT_WIDTH }}
      >
        <Controller
          control={control}
          name="tops_size"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="tops_size"
                options={getCatalogOptions(tops_size?.options)}
                value={value}
                label="What is your usual size in tops?"
                placeholder="What is your usual size in tops?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(tops_size, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="pants_size_w"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="pants_size_w"
                options={getCatalogOptions(pants_size_w?.options)}
                value={value}
                label="What is your usual size in bottoms / pants (women's apparel)?"
                placeholder="What is your usual size in bottoms / pants (women's apparel)?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(pants_size_w, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="pants_size_m"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="pants_size_m"
                options={getCatalogOptions(pants_size_m?.options)}
                value={value}
                label="What is your usual size in bottoms / pants (men's apparel)?"
                placeholder="What is your usual size in bottoms / pants (men's apparel)?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(pants_size_m, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="pants_type"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="pants_type"
                options={getCatalogOptions(pants_type?.options)}
                value={value}
                label="What fit of pants do you typically prefer?"
                placeholder="What fit of pants do you typically prefer?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(pants_type, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="dress_size"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="dress_size"
                options={getCatalogOptions(dress_size?.options)}
                value={value}
                label="What is your usual US dress size?"
                placeholder="What is your usual US dress size?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(dress_size, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="suits_size"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="suits_size"
                options={getCatalogOptions(suits_size?.options)}
                value={value}
                label="What is your usual suit size?"
                placeholder="What is your usual suit size?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(suits_size, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="height"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="height"
                options={getCatalogOptions(height?.options)}
                value={value}
                label="What is your approximate height?"
                placeholder="What is your approximate height?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(height, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="weight"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="weight"
                options={getCatalogOptions(weight?.options)}
                value={value}
                label="Which of the following is closest to your body weight?"
                placeholder="Which of the following is closest to your body weight?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(weight, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="body_type"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const isMultiple = body_type?.optionType === 'MULTIPLE'
            const value = getAttrSelectValue(
              isMultiple,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                isMulti={isMultiple}
                name="body_type"
                options={getCatalogOptions(body_type?.options)}
                value={value}
                label="How would you describe your body type and shape? Please select all that apply!"
                placeholder="How would you describe your body type and shape? Please select all that apply!"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(body_type, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="shoe_size_new"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="shoe_size_new"
                options={getCatalogOptions(shoe_size_new?.options)}
                value={value}
                label="What is your usual shoe size?"
                placeholder="What is your usual shoe size?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(shoe_size_new, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="foot_width"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="foot_width"
                options={getCatalogOptions(foot_width?.options)}
                value={value}
                label="How would you describe the width of your foot?"
                placeholder="How would you describe the width of your foot?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(foot_width, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="ear_piercings"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="ear_piercings"
                options={getCatalogOptions(ear_piercings?.options)}
                value={value}
                label="Do you have your ears pierced? If so, how many ear piercings do you have?"
                placeholder="Do you have your ears pierced? If so, how many ear piercings do you have?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(ear_piercings, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="ear_piercing_types"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const isMultiple = ear_piercing_types?.optionType === 'MULTIPLE'
            const value = getAttrSelectValue(
              isMultiple,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                isMulti={isMultiple}
                name="ear_piercing_types"
                options={getCatalogOptions(ear_piercing_types?.options)}
                value={value}
                label="Do you have any of the following kinds of ear piercings?"
                placeholder="Do you have any of the following kinds of ear piercings?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(ear_piercing_types, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="body_piercings"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const isMultiple = body_piercings?.optionType === 'MULTIPLE'
            const value = getAttrSelectValue(
              isMultiple,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                isMulti={isMultiple}
                name="body_piercings"
                options={getCatalogOptions(body_piercings?.options)}
                value={value}
                label="Do you have any of the following body piercings?"
                placeholder="Do you have any of the following body piercings?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(body_piercings, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="bra_band_size"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="bra_band_size"
                options={getCatalogOptions(bra_band_size?.options)}
                value={value}
                label="What is your typical bra band size?"
                placeholder="What is your typical bra band size?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(bra_band_size, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="bra_cup_size"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="bra_cup_size"
                options={getCatalogOptions(bra_cup_size?.options)}
                value={value}
                label="What is your typical bra cup size?"
                placeholder="What is your typical bra cup size?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(bra_cup_size, e, field.onChange)
                }}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="shapewear_size"
          defaultValue={null}
          render={({ field, fieldState }) => {
            const value = getAttrSelectValue(
              false,
              field.value?.value as CatalogItem | CatalogItem[]
            )

            return (
              <ReactSelect
                name="shapewear_size"
                options={getCatalogOptions(shapewear_size?.options)}
                value={value}
                label="Do you use shapewear or waist trainers? If so, what size do you typically wear in shapewear?"
                placeholder="Do you use shapewear or waist trainers? If so, what size do you typically wear in shapewear?"
                error={fieldState.error?.message}
                margin="dense"
                isLoading={isLoading}
                onChange={(e) => {
                  onChangeAttribute(shapewear_size, e, field.onChange)
                }}
              />
            )
          }}
        />
      </Grid>
      <Grid container mt={5}>
        <PostponedFormActionButtons
          onBack={() => onStepChange(step.id - 1)}
          onSubmit={onSubmit}
        />
      </Grid>
    </>
  )
}

export default SizingPreferencesForm
